/**
 * slideshow.ts — Kiosk slideshow configuration
 *
 * This is the only file you need to edit to customise the /leaderboard kiosk display.
 *
 * The screen cycles through slides in this order:
 *   Leaderboard → contentPages[0] → contentPages[1] → ... → back to Leaderboard
 *
 * Timings are controlled by:
 *   leaderboardDuration  — how long the leaderboard is shown (ms)
 *   contentPageDuration  — how long each content page is shown (ms)
 *
 * During the leaderboard window the list auto-scrolls to fill the full duration.
 *
 * ── Dev tip ──────────────────────────────────────────────────────────────────
 * Append ?pageid=<id> to the URL to pin to a specific slide and disable rotation.
 * Examples:
 *   /leaderboard?pageid=leaderboard
 *   /leaderboard?pageid=ghostpay
 *   /leaderboard?pageid=about
 *
 * ── Content page types ───────────────────────────────────────────────────────
 *
 * 1. CENTRED (default — omit `type`)
 *    Full-screen slide with a large icon, heading, and body text centred on screen.
 *    Required: backgroundColor, icon, title, content
 *    Example:
 *      {
 *        id: 'about',
 *        backgroundColor: '#0a0d1a',
 *        icon: '🤖',
 *        title: 'Your Headline Here',
 *        content: 'Supporting copy goes here.',
 *      }
 *
 * 2. SPLIT_RIGHT (type: 'split_right')
 *    Text + icon on the left, portrait video on the right.
 *    Required: backgroundColor, icon, title, content, videoUrl
 *    videoUrl should be a path relative to /public (e.g. '/demo.mp4')
 *    Example:
 *      {
 *        id: 'demo-right',
 *        type: 'split_right',
 *        backgroundColor: '#0a0f1f',
 *        icon: '🎬',
 *        title: 'See It In Action',
 *        content: 'Watch a live fraud pattern unfold.',
 *        videoUrl: '/fraud-demo.mp4',
 *      }
 *
 * 3. SPLIT_LEFT (type: 'split_left')
 *    Portrait video on the left, text + icon on the right.
 *    Required: backgroundColor, icon, title, content, videoUrl
 *    Example:
 *      {
 *        id: 'demo-left',
 *        type: 'split_left',
 *        backgroundColor: '#1a0a0a',
 *        icon: '📊',
 *        title: 'Real-Time Analysis',
 *        content: 'AI assesses risk before you decide.',
 *        videoUrl: '/analysis-demo.mp4',
 *      }
 *
 * 4. HTML / IFRAME (type: 'html')
 *    Full-viewport raw HTML — use for Google Slides embeds, iframes, or custom markup.
 *    Required: backgroundColor, html
 *    Example:
 *      {
 *        id: 'slides',
 *        type: 'html',
 *        backgroundColor: '#000000',
 *        html: `<iframe
 *          src="https://docs.google.com/presentation/d/e/YOUR_ID/pubembed?start=true&loop=true&delayms=5000"
 *          frameborder="0" width="1920" height="1080"
 *          allowfullscreen>
 *        </iframe>`,
 *      }
 */

export interface ContentPage {
  id: string;
  type?: 'split_right' | 'split_left' | 'html';
  backgroundColor: string;
  icon?: string;
  title?: string;
  content?: string;
  videoUrl?: string;
  html?: string;
}

export interface SlideshowConfig {
  leaderboardDuration: number;  // ms to show leaderboard before advancing
  contentPageDuration: number;  // ms per content page
  contentPages: ContentPage[];
}

export const slideshowConfig: SlideshowConfig = {
  leaderboardDuration: 30000,
  contentPageDuration: 12000,

  contentPages: [
    {
      id: 'about',
      backgroundColor: '#1d1d1b', // TW charcoal
      icon: '🤖',
      title: 'AI vs Human: Fraud Detection',
      content: 'Review 10 real-looking bank transactions and decide — Approve or Flag as Fraud. Then see how Amazon Bedrock (Claude Haiku) assessed the exact same transactions, independently. Where do human instinct and machine intelligence agree?',
    },
    {
      id: 'slide_deck_2',
      type: 'html',
      backgroundColor: '#000000',
      html: `<iframe src="https://docs.google.com/presentation/d/e/2PACX-1vSB3FSKI9vF_hh3XJu5d18Bi42eRuo7k-8BZPkaPrWTK3IoIiADU2AN-NdlfLxtqvrOA0fc_VPUcS-g/pubembed?start=true&loop=true&delayms=5000&rm=minimal" frameborder="0" width="1440" height="839" allowfullscreen="true" mozallowfullscreen="true" webkitallowfullscreen="true"></iframe>`,
    },
    {
      id: 'ghostpay',
      backgroundColor: '#2a0018', // TW magenta-dark
      icon: '👻',
      title: 'GhostPay',
      content: 'Card-not-present fraud and international money laundering. A stolen identity funding purchases across multiple countries — each transaction plausible in isolation, suspicious as a pattern.',
    },
    {
      id: 'slide_deck_3',
      type: 'html',
      backgroundColor: '#000000',
      html: `<iframe src="https://docs.google.com/presentation/d/e/2PACX-1vSlfVP958_oSxtzMXFAbM73JL6QNUcMkeN3L_1WR1Vp_U1HBPM4XCMMuEGAwj7bZ_rCOv6cZgKtaf1w/pubembed?start=true&loop=true&delayms=5000&rm=minimal" frameborder="0" width="1440" height="839" allowfullscreen="true" mozallowfullscreen="true" webkitallowfullscreen="true"></iframe>`,
    },
    {
      id: 'identityshift',
      backgroundColor: '#2a0a12', // TW flamingo-dark
      icon: '🎭',
      title: 'IdentityShift',
      content: 'Synthetic identity creation and bust-out fraud. A fictitious person assembled from real data, steadily building credit history before maxing out and vanishing overnight.',
    },
    {
      id: 'slide_deck_1',
      type: 'html',
      backgroundColor: '#000000',
      html: `<iframe src="https://docs.google.com/presentation/d/e/2PACX-1vSXK1Ucy3YQfzxWrrrtOpJfO3dtBqp3skKQ8kkhdle6H7Lh06KfHC5yWdyQzkS3CgMJkq-Sj-eCXFJB/pubembed?start=true&loop=true&delayms=5000&rm=minimal" frameborder="0" width="1440" height="839" allowfullscreen="true" mozallowfullscreen="true" webkitallowfullscreen="true"></iframe>`,
    },    
    {
      id: 'shadowmerchant',
      backgroundColor: '#001e27', // TW deep teal
      icon: '🏪',
      title: 'ShadowMerchant',
      content: 'Ghost merchant collusion and refund cycling. A network of fake businesses processing fraudulent refunds to quietly extract cash — hiding in plain sight among legitimate transactions.',
    },
  ],
};